import React, { useState } from 'react';
import { useRegime } from '../../contexts/RegimeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { regimeMeta } from '../../utils/regimeTheme';

const BADGE_Z = 10060;
/** Above the fetch-log button, and above the Simple-mode tab bar (the shell publishes its height). */
const BADGE_BOTTOM = 'calc(var(--mmec-bottom-offset, 0px) + 72px + env(safe-area-inset-bottom, 0px))';

/**
 * Always-on floating regime emoji. Can be minimized; regime tint stays on document root.
 */
const RegimeFloatingBadge: React.FC = () => {
  const { regime, badgeHidden, setBadgeHidden, snapshot, loading, error } = useRegime();
  const { language } = useLanguage();
  const { colors, isDarkMode } = useTheme();
  const ar = language === 'ar';
  const meta = regimeMeta(regime);
  const [expanded, setExpanded] = useState(false);

  const anchorStyle: React.CSSProperties = {
    position: 'fixed',
    // Clear the Pro nav rail (the shell publishes its width); 0 in Simple mode.
    left: "calc(var(--mmec-rail-offset, 0px) + 16px)",
    bottom: BADGE_BOTTOM,
    zIndex: BADGE_Z,
    pointerEvents: 'auto',
  };

  if (badgeHidden) {
    return (
      <button
        type="button"
        data-mmec-regime-badge="restore"
        title={ar ? 'إظهار مؤشر النظام' : 'Show regime indicator'}
        onClick={() => setBadgeHidden(false)}
        style={{
          ...anchorStyle,
          width: 40,
          height: 40,
          borderRadius: 20,
          border: `2px solid ${meta.accent}`,
          background: isDarkMode ? 'rgba(12,16,24,0.96)' : 'rgba(255,255,255,0.98)',
          boxShadow: `0 0 16px ${meta.glow}`,
          cursor: 'pointer',
          fontSize: 20,
          lineHeight: 1,
        }}
      >
        {meta.emoji}
      </button>
    );
  }

  const risk = snapshot?.risk_level ?? 'LOW';
  const conf = snapshot ? Math.round(snapshot.regime_confidence * 100) : null;
  const factors = snapshot?.factors;
  const spikeProb = typeof factors?.iv_spike_probability === 'number' ? factors.iv_spike_probability : null;
  const spikeLive = factors?.iv_spike_live === true;
  const spikeHot = spikeLive || (spikeProb != null && spikeProb >= 0.5);

  return (
    <div
      data-mmec-regime-badge="open"
      style={{
        ...anchorStyle,
        minWidth: expanded ? 240 : 56,
        maxWidth: 300,
        borderRadius: 14,
        border: `2px solid ${meta.accent}`,
        background: isDarkMode ? 'rgba(10,14,22,0.97)' : 'rgba(255,255,255,0.98)',
        boxShadow: `0 8px 32px ${meta.glow}`,
        color: colors.text,
        transition: 'min-width 0.2s ease',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: expanded ? '10px 12px' : '10px 12px',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setExpanded((v) => !v)}
      >
        <span style={{ fontSize: 28, lineHeight: 1 }} aria-hidden>
          {meta.emoji}
        </span>
        {expanded && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: meta.accent }}>
              {ar ? meta.labelAr : meta.labelEn}
              {loading ? ' …' : ''}
            </div>
            <div style={{ fontSize: 11, opacity: 0.85 }}>
              {ar ? 'مخاطر' : 'Risk'}: {risk}
              {conf != null ? ` · ${conf}%` : ''}
            </div>
            {error && (
              <div style={{ fontSize: 10, color: '#f0b429', marginTop: 4 }}>
                {ar ? 'تحليلات مؤقتة — عرض افتراضي' : 'Analytics pending — showing default'}
              </div>
            )}
            {snapshot?.gex.trap_zone && (
              <div style={{ fontSize: 10, color: '#ff4d6d', marginTop: 4 }}>
                {ar ? '⚠ منطقة فخ جاما' : '⚠ Gamma trap zone'}
              </div>
            )}
            {spikeHot && (
              <div style={{ fontSize: 10, color: '#e0533d', marginTop: 4 }}>
                {ar
                  ? `⚡ احتمال قفزة تقلب${spikeProb != null ? ` ${Math.round(spikeProb * 100)}%` : ''}`
                  : `⚡ Vol spike risk${spikeProb != null ? ` ${Math.round(spikeProb * 100)}%` : ''}${spikeLive ? ' · live' : ''}`}
              </div>
            )}
          </div>
        )}
        {!expanded && spikeHot && !loading && (
          <span title="Vol spike risk" style={{ fontSize: 13, lineHeight: 1 }} aria-hidden>
            ⚡
          </span>
        )}
        {!expanded && loading && (
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              background: meta.accent,
              opacity: 0.8,
              animation: 'pulse 1.2s ease-in-out infinite',
            }}
            aria-hidden
          />
        )}
        <button
          type="button"
          title={ar ? 'إخفاء' : 'Hide'}
          onClick={(e) => {
            e.stopPropagation();
            setBadgeHidden(true);
            setExpanded(false);
          }}
          style={{
            border: 'none',
            background: 'transparent',
            color: colors.textSecondary,
            cursor: 'pointer',
            fontSize: 16,
            padding: 4,
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default RegimeFloatingBadge;
